import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { FaArrowLeft, FaCog, FaEye, FaEyeSlash, FaList, FaPlay, FaExpand, FaSync, FaDownload, FaVolumeUp, FaVolumeMute, FaStop, FaChevronDown, FaChevronRight } from "react-icons/fa";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Collapse from "react-bootstrap/Collapse";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import Form from "react-bootstrap/Form";
import "../App.css";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  MISSION_IMAGE_RATING_LABELS,
  MISSION_BUDGET_GRAPH_SYSTEMS,
  MISSION_BUDGET_PROMPT_INDEX,
} from "../mission/missionConfig";
import { getMissionByNode } from "../mission/missions";

/** Page shell: mission = starfield; film = same cream as `.film-theme` (no film card overrides). */
function SlideshowPageRoot({ useMissionBackground, useFilmBackground, children }) {
  if (useMissionBackground) return <div className="slideshow-mission-page">{children}</div>;
  if (useFilmBackground) return <div className="slideshow-film-page">{children}</div>;
  return <>{children}</>;
}

/** Matches slideshow header accent (`orangeHeader`). */
const SLIDESHOW_ORANGE = "#f05f40";

/** Unselected Film/Mission label (thin weight) on orange bar — light orange, not white. */
const SLIDESHOW_HEADER_UNSELECTED_TEXT = "#ffd4c4";

/** Mission analytics bar charts: wider card, shorter bars / tighter rows. */
const ANALYTICS_CHART_MAX_WIDTH = "960px";
const ANALYTICS_BAR_TRACK_HEIGHT = 8;

/** Matches Bootstrap `my-4` top margin — used inside sticky strip so gap does not scroll away before header sticks. */
const WORKSHOP_OVERVIEW_TOP_GAP = "1.5rem";

// Utility function to detect if an image is blank/white
const detectBlankSlide = (imageUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      let transparentPixels = 0;
      let totalPixels = data.length / 4;
      
      // Sample pixels to improve performance (check every 10th pixel)
      for (let i = 0; i < data.length; i += 40) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];
        
        // Check if pixel is transparent (alpha = 0)
        if (a === 0) {
          transparentPixels++;
        }
      }
      
      const transparentPercentage = (transparentPixels / (totalPixels / 10)) * 100;
      resolve(transparentPercentage > 95); // Consider blank if >95% transparent
    };
    img.onerror = () => {
      resolve(false); // If image fails to load, assume not blank
    };
    img.src = imageUrl;
  });
};

// Utility function to extract slide number from URL
const extractSlideNumber = (imageUrl) => {
  // Extract the slide_id from the URL
  // URL format: /download-slide?password=...&workshop_id=...&slide_id=filename.png
  const urlParams = new URLSearchParams(imageUrl.split('?')[1]);
  const slideId = urlParams.get('slide_id');
  
  if (slideId) {
    // Remove .png extension and return the slide number
    return slideId.replace('.png', '');
  }
  
  // Fallback: try to extract from the URL path
  const match = imageUrl.match(/slide_id=([^.]+)/);
  return match ? match[1] : 'Unknown';
};

/** Native primary control: fixed 8px radius + min size (avoids circular look when squeezed; same idea as Film/Mission). */
const VIEW_SLIDESHOW_BTN_STYLE = {
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundColor: '#0d6efd',
  border: '1px solid #0d6efd',
  color: '#fff',
  borderRadius: '8px',
  margin: 0,
  padding: '0.45rem 0.85rem',
  fontSize: 'clamp(0.75rem, 2.2vw, 1rem)',
  fontWeight: 500,
  fontFamily: 'Roboto, sans-serif',
  lineHeight: 1.25,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  minWidth: '10.5rem',
  minHeight: '2.35rem',
  boxSizing: 'border-box',
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const VIEW_SLIDESHOW_BTN_STYLE_LG = {
  ...VIEW_SLIDESHOW_BTN_STYLE,
  padding: '0.55rem 1rem',
  fontSize: 'clamp(0.82rem, 2.2vw, 1.1rem)',
  minHeight: '2.75rem',
  minWidth: '11.5rem',
};

const Slideshow = (props) => {
  const isMissionSlideshow = props.config.workshopType === 'mission';
  const isFilmWorkshopSlideshow = props.config.workshopType === 'film';

  const [images, setImages] = useState([]);
  const [allWorkshopIds, setAllWorkshopIds] = useState(null);
  const [workshopLoadError, setWorkshopLoadError] = useState(null);
  const [submittedWorkshopId, setSubmittedWorkshopId] = useState(null);
  const [loadingSlides, setLoadingSlides] = useState(false);
  const [refreshingSlides, setRefreshingSlides] = useState(false);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [blankSlides, setBlankSlides] = useState([]);
  const [analyzingSlides, setAnalyzingSlides] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [cacheBuster, setCacheBuster] = useState(0);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [blankDetectionEnabled, setBlankDetectionEnabled] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioVolume, setAudioVolume] = useState(0.7);
  const [audioMuted, setAudioMuted] = useState(false);
  const [audioDurations, setAudioDurations] = useState({});
  const [showInactiveWorkshops, setShowInactiveWorkshops] = useState(false);
  const [ratingCounts, setRatingCounts] = useState({});
  const [ratingTotal, setRatingTotal] = useState(0);
  const [ratingCountsLoading, setRatingCountsLoading] = useState(false);
  const [ratingCountsError, setRatingCountsError] = useState(null);
  const [budgetAverages, setBudgetAverages] = useState({});
  const [budgetAvgCount, setBudgetAvgCount] = useState(0);
  const [budgetAvgLoading, setBudgetAvgLoading] = useState(false);
  const [budgetAvgError, setBudgetAvgError] = useState(null);
  const [slideLoadError, setSlideLoadError] = useState(null);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(true);
  const audioRef = useRef(null);
  const slideshowBrandRef = useRef(null);
  const slideshowSubtitleNaturalRef = useRef(null);
  const orangeHeaderRef = useRef(null);
  const orangeHeaderHeightRef = useRef(-1);
  const [slideshowSubtitleFontPx, setSlideshowSubtitleFontPx] = useState(10);
  const [workshopCardHeaderStickyTopPx, setWorkshopCardHeaderStickyTopPx] = useState(48);
  /** True while the image gallery is in browser fullscreen (avoid unmounting it before fullscreen exits). */
  const slideshowFullscreenActiveRef = useRef(false);
  const slideshowViewerRef = useRef(null);

  // Orange bar height → offset for sticky workshop card heading (sits just below sticky header).
  // Guard updates so tiny ResizeObserver / subpixel changes do not re-render and jitter the sticky bar.
  useLayoutEffect(() => {
    orangeHeaderHeightRef.current = -1;
    const el = orangeHeaderRef.current;
    if (!el) return;
    const sync = () => {
      const h = Math.round(el.offsetHeight);
      if (h === orangeHeaderHeightRef.current) return;
      orangeHeaderHeightRef.current = h;
      setWorkshopCardHeaderStickyTopPx(h);
    };
    sync();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(sync);
    });
    ro.observe(el);
    window.addEventListener("resize", sync);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [submittedWorkshopId]);

  // Match subtitle line width to brand line (same base scale as 10px → scaled).
  useLayoutEffect(() => {
    const basePx = 10;
    const measure = () => {
      const b = slideshowBrandRef.current;
      const n = slideshowSubtitleNaturalRef.current;
      if (!b || !n) return;
      const tw = b.getBoundingClientRect().width;
      const w = n.getBoundingClientRect().width;
      if (w < 0.01 || tw < 0.01) return;
      const next = (basePx * tw) / w;
      setSlideshowSubtitleFontPx((prev) => (Math.abs(prev - next) < 0.02 ? prev : next));
    };
    measure();
    const parent = slideshowBrandRef.current?.parentElement;
    const ro = parent ? new ResizeObserver(measure) : null;
    if (parent && ro) ro.observe(parent);
    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [props.config.brand, props.config.subtitle]);

  const openManageServers = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const host = window.location.hostname;
    const port = window.location.port;
    const isLocalDev = (host === 'localhost' || host === '127.0.0.1') && (port === '3000' || port === '3001');
    const managePort = port === '3001' ? '3000' : '3001';
    const manageUrl = isLocalDev
      ? `http://${host}:${managePort}/manage-servers/`
      : '/manage-servers/';
    window.open(manageUrl, '_blank', 'noopener,noreferrer');
  };

  // Fetch list of workshops on mount.
  useEffect(() => {
    document.title = `Slideshow | ${props.config.subtitle} | ${props.config.brand}`;
    fetchWorkshops();
  }, [showInactiveWorkshops]);

  const fetchWorkshops = () => {
    setWorkshopLoadError(null);
    const endpoint = props.config.workshopType === 'mission' ? '/list-mission-workshops' : '/list-workshops';
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        active_only: !showInactiveWorkshops,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.error) {
          setWorkshopLoadError(data.error);
          setAllWorkshopIds([]);
        } else if (data.workshops) {
          setAllWorkshopIds(data.workshops);
          setWorkshopLoadError(null);
        } else {
          setAllWorkshopIds([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching workshops:", error);
        setWorkshopLoadError('Failed to load workshops: ' + error.message);
        setAllWorkshopIds([]);
      });
  };

  // Function to get audio duration
  const getAudioDuration = (audioUrl) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener('loadedmetadata', () => {
        resolve(audio.duration);
      });
      audio.addEventListener('error', () => {
        resolve(0);
      });
      audio.src = audioUrl;
    });
  };

  // Function to format duration in MM:SS format
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Function to fetch slides for a workshop
  const fetchSlides = async (workshopId, cacheBusterDate) => {
    const requestData = {
      workshop_id: workshopId,
    };

    setSlideLoadError(null);

    try {
      const response = await fetch("/get-slides", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const submissions = data.submissions || [];
      setSubmissions(submissions);
      
      // Extract slides from submissions
      const slides = submissions
        .filter(submission => submission.slide !== null)
        .map(submission => submission.slide);
      
      // Add cache-busting parameter to image URLs (always use it)
      let imageItems = slides.map((slide) => {
        const imageUrl = `${slide}${slide.includes('?') ? '&' : '?'}t=${cacheBusterDate}`;
        return { original: imageUrl };
      });
      
      // Add cache-busting parameter to audio URLs as well
      const submissionsWithCacheBuster = submissions.map(submission => ({
        ...submission,
        audio: submission.audio ? `${submission.audio}${submission.audio.includes('?') ? '&' : '?'}t=${cacheBusterDate}` : null
      }));
      
      // Sort slides alphabetically by slide number
      imageItems.sort((a, b) => {
        const slideNumberA = extractSlideNumber(a.original);
        const slideNumberB = extractSlideNumber(b.original);
        return slideNumberA.localeCompare(slideNumberB);
      });
      
      // Sort submissions by student_id so order is consistent with imageItems
      const sortedSubmissions = submissionsWithCacheBuster.sort((a, b) => {
        const idA = a.student_id || '';
        const idB = b.student_id || '';
        return idA.localeCompare(idB);
      });
      
      setImages(imageItems);
      setSubmissions(sortedSubmissions);

      // Set loading to false immediately after basic slide data is loaded
      setLoadingSlides(false);

      // Load audio durations in the background
      const durations = {};
      for (const submission of sortedSubmissions) {
        if (submission.audio) {
          try {
            const duration = await getAudioDuration(submission.audio);
            durations[submission.audio] = duration;
          } catch (error) {
            console.error('Error loading audio duration:', error);
            durations[submission.audio] = 0;
          }
        }
      }
      setAudioDurations(durations);

      let imageSlides = [];
      for (let i = 0; i < imageItems.length; i++) {
        imageSlides.push(imageItems[i].original);
      }

      // Only analyze slides for blank detection if enabled
      if (blankDetectionEnabled) {
        // Run blank detection in the background without blocking the UI
        analyzeSlidesForBlank(imageSlides);
      } else {
        setBlankSlides([]); // Clear blank slides when detection is disabled
      }
      
      return slides;
    } catch (error) {
      console.error("Error fetching slides:", error);
      setSlideLoadError(error.message);
      throw error;
    }
  };

  // Fetch slides whenever a workshop is submitted.
  useEffect(() => {
    if (!submittedWorkshopId) return;

    setLoadingSlides(true);
    setSlideLoadError(null);
    const cacheBusterDate = Date.now();
    setCacheBuster(cacheBusterDate);
    fetchSlides(submittedWorkshopId, cacheBusterDate)
      .catch(() => {
        setLoadingSlides(false);
      });
  }, [submittedWorkshopId]);

  // Refresh slides function
  const handleRefreshSlides = async () => {
    if (!submittedWorkshopId) return;
    
    setRefreshingSlides(true);
    setBlankSlides([]); // Clear blank slides immediately when refreshing
    setAudioDurations({}); // Clear audio durations to force reload
    // Update cache buster to force re-render of all images
    const cacheBusterDate = Date.now();
    setCacheBuster(cacheBusterDate);
    try {
      // Force refresh with cache-busting
      await fetchSlides(submittedWorkshopId, cacheBusterDate);
    } catch (error) {
      // Error already logged in fetchSlides
    } finally {
      setRefreshingSlides(false);
    }
  };

  const fetchMissionImageRatings = useCallback(async () => {
    if (!submittedWorkshopId || !isMissionSlideshow) return;
    setRatingCountsLoading(true);
    setRatingCountsError(null);
    try {
      const res = await fetch("/get-mission-image-ratings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workshop_id: submittedWorkshopId }),
      });
      const text = await res.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(
          "Could not load ratings (server returned a non-JSON page). Confirm the API is running and restarted after updates."
        );
      }
      if (!res.ok) {
        throw new Error(data.error || "Failed to load ratings");
      }
      setRatingCounts(data.counts || {});
      setRatingTotal(typeof data.total === "number" ? data.total : 0);
    } catch (e) {
      setRatingCountsError(e.message || "Error");
    } finally {
      setRatingCountsLoading(false);
    }
  }, [submittedWorkshopId, isMissionSlideshow]);

  const fetchMissionBudgetAverages = useCallback(async () => {
    if (!submittedWorkshopId || !isMissionSlideshow) return;
    setBudgetAvgLoading(true);
    setBudgetAvgError(null);
    try {
      const res = await fetch("/get-mission-budget-averages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workshop_id: submittedWorkshopId,
          prompt_index: MISSION_BUDGET_PROMPT_INDEX,
        }),
      });
      const text = await res.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(
          "Could not load budget averages (server returned a non-JSON page). Confirm the API is running and restarted after updates."
        );
      }
      if (!res.ok) {
        throw new Error(data.error || "Failed to load budget averages");
      }
      setBudgetAverages(data.averages || {});
      setBudgetAvgCount(typeof data.count === "number" ? data.count : 0);
    } catch (e) {
      setBudgetAvgError(e.message || "Error");
    } finally {
      setBudgetAvgLoading(false);
    }
  }, [submittedWorkshopId, isMissionSlideshow]);

  const refreshMissionAnalytics = useCallback(() => {
    fetchMissionImageRatings();
    fetchMissionBudgetAverages();
  }, [fetchMissionImageRatings, fetchMissionBudgetAverages]);

  const analyticsRefreshing = ratingCountsLoading || budgetAvgLoading;

  const overviewRefreshBusy =
    refreshingSlides || (isMissionSlideshow && analyticsRefreshing);

  const handleRefreshOverview = async () => {
    if (!submittedWorkshopId) return;
    if (isMissionSlideshow) {
      refreshMissionAnalytics();
    }
    await handleRefreshSlides();
  };

  useEffect(() => {
    if (!isMissionSlideshow || !submittedWorkshopId || showSlideshow) return;
    fetchMissionImageRatings();
    fetchMissionBudgetAverages();
  }, [
    isMissionSlideshow,
    submittedWorkshopId,
    showSlideshow,
    fetchMissionImageRatings,
    fetchMissionBudgetAverages,
  ]);

  // Analyze slides to detect blank/white slides
  const analyzeSlidesForBlank = async (slides) => {
    
    setAnalyzingSlides(true);
    const blankIndices = [];
    
    for (let i = 0; i < slides.length; i++) {
      const isBlank = await detectBlankSlide(slides[i]);
      if (isBlank) {
        blankIndices.push(i + 1); // +1 for 1-based indexing
      }
    }
    setBlankSlides(blankIndices);
    setAnalyzingSlides(false);
  };

  // Function to handle audio playback
  const playAudio = (audioUrl) => {
    if (audioRef.current) {
      // Stop current audio if playing
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    if (audioUrl) {
      setCurrentAudio(audioUrl);
      audioRef.current.src = audioUrl;
      audioRef.current.volume = audioMuted ? 0 : audioVolume;
      audioRef.current.play().then(() => {
        setIsAudioPlaying(true);
      }).catch(error => {
        console.error('Error playing audio:', error);
        setIsAudioPlaying(false);
      });
    } else {
      setCurrentAudio(null);
      setIsAudioPlaying(false);
    }
  };

  // Function to stop audio
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsAudioPlaying(false);
  };

  // Handle audio ended event
  const handleAudioEnded = () => {
    setIsAudioPlaying(false);
  };

  // Function to play audio for current slide during slideshow
  const playAudioForCurrentSlide = () => {
    if (!showSlideshow) return;
    
    const filteredSlides = getFilteredSlides();
    if (currentSlideIndex >= 0 && currentSlideIndex < filteredSlides.length) {
      const currentSlide = filteredSlides[currentSlideIndex];
      const slideNumber = extractSlideNumber(currentSlide.original);
      
      // Find submission with matching slide
      const submission = submissions.find(sub => {
        if (!sub.slide) return false;
        const subSlideNumber = extractSlideNumber(sub.slide);
        return subSlideNumber === slideNumber;
      });
      
      if (submission && submission.audio) {
        playAudio(submission.audio);
      } else {
        stopAudio();
        setCurrentAudio(null);
      }
    }
  };

  // Effect to play audio when slide changes during slideshow
  useEffect(() => {
    if (showSlideshow) {
      playAudioForCurrentSlide();
    }
  }, [currentSlideIndex, showSlideshow]);

  // Effect to stop audio when leaving slideshow
  useEffect(() => {
    if (!showSlideshow) {
      stopAudio();
    }
  }, [showSlideshow]);

  // Function to handle clicking on a slide thumbnail — opens fullscreen slideshow at that slide
  const handleSlideClick = (slideIndex) => {
    const nonBlankSlides = images.filter((_, index) => !blankSlides.includes(index + 1));
    const filteredIndex = nonBlankSlides.findIndex((_, index) => {
      let originalIndex = 0;
      let filteredCount = 0;
      while (filteredCount <= index) {
        if (!blankSlides.includes(originalIndex + 1)) {
          filteredCount++;
        }
        originalIndex++;
      }
      return originalIndex - 1 === slideIndex;
    });

    const startIndex = Math.max(0, filteredIndex);
    setCurrentSlideIndex(startIndex);
    setShowSlideshow(true);

    setTimeout(() => {
      const slideshowContainer = document.querySelector('.image-gallery');
      if (slideshowContainer && slideshowContainer.requestFullscreen) {
        slideshowContainer
          .requestFullscreen()
          .then(() => {
            slideshowFullscreenActiveRef.current = true;
          })
          .catch((err) => {
            console.log('Fullscreen request failed:', err);
          });
      }
    }, 100);
  };

  // Get filtered slides for slideshow (excluding blank slides)
  const getFilteredSlides = () => {
    if (!images || images.length === 0) {
      return []; // Return empty array if no images
    }
    if (!blankDetectionEnabled) {
      return images; // Return all slides if blank detection is disabled
    }
    return images.filter((_, index) => !blankSlides.includes(index + 1));
  };

  // Function to enter fullscreen slideshow
  const enterFullscreenSlideshow = () => {
    setCurrentSlideIndex(0);
    setShowSlideshow(true);

    // Request fullscreen after a short delay to ensure slideshow is rendered
    setTimeout(() => {
      const slideshowContainer = document.querySelector('.image-gallery');
      if (slideshowContainer && slideshowContainer.requestFullscreen) {
        slideshowContainer
          .requestFullscreen()
          .then(() => {
            slideshowFullscreenActiveRef.current = true;
          })
          .catch((err) => {
            console.log('Fullscreen request failed:', err);
          });
      }
    }, 100);
  };

  /** Leave slideshow view; must not unmount the gallery while it is still the fullscreen element. */
  const exitSlideshowToOverview = useCallback(() => {
    const finish = () => {
      slideshowFullscreenActiveRef.current = false;
      setShowSlideshow(false);
    };
    if (document.fullscreenElement) {
      document.exitFullscreen().then(finish).catch(finish);
    } else {
      finish();
    }
  }, []);

  // ESC: back to overview. When fullscreen is active, unmount only after exitFullscreen completes (see exitSlideshowToOverview).
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showSlideshow) {
        exitSlideshowToOverview();
      }
    };

    if (showSlideshow) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSlideshow, exitSlideshowToOverview]);

  // If the browser exits fullscreen (e.g. Escape) before our key handler runs, still return to overview.
  useEffect(() => {
    if (!showSlideshow) return undefined;
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && slideshowFullscreenActiveRef.current) {
        slideshowFullscreenActiveRef.current = false;
        setShowSlideshow(false);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [showSlideshow]);

  useEffect(() => {
    if (showSlideshow && slideshowViewerRef.current) {
      slideshowViewerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showSlideshow]);

  // Function to download slideshow as PDF
  const downloadSlideshowAsPDF = async () => {
    setDownloadingPDF(true);
    
    try {
      // Use square format (200mm x 200mm) instead of A4 landscape
      const pdf = new jsPDF('p', 'mm', [200, 200]); // Square format
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Filter out blank slides if user wants to exclude them and detection is enabled
      const slidesToInclude = blankDetectionEnabled 
        ? images.filter((_, index) => !blankSlides.includes(index + 1))
        : images;
      
      for (let i = 0; i < slidesToInclude.length; i++) {
        const slide = slidesToInclude[i];
        
        try {
          // Create a temporary image element
          const img = new Image();
          img.crossOrigin = "anonymous";
          
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = slide.original;
          });
          
          // Create a canvas to draw the image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas dimensions to match the image
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw the image on canvas
          ctx.drawImage(img, 0, 0);
          
          // Convert canvas to image data
          const imgData = canvas.toDataURL('image/jpeg', 0.95);
          
          // Remove all borders - fill the entire page
          const x = 0;
          const y = 0;
          const width = pageWidth;
          const height = pageHeight;
          
          // Add new page for each slide (except the first one)
          if (i > 0) {
            pdf.addPage();
          }
          
          // Add the image to PDF, filling the entire page with no borders
          pdf.addImage(imgData, 'JPEG', x, y, width, height);
          
        } catch (error) {
          console.error(`Error processing slide ${i + 1}:`, error);
          // Continue with next slide even if one fails
        }
      }
      
      // Save the PDF
      const filename = `${submittedWorkshopId}_slideshow.pdf`;
      pdf.save(filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Function to download MP3 file
  const downloadMP3 = async (audioUrl, studentId) => {
    try {
      // Extract the audio_id from the URL
      const urlParams = new URLSearchParams(audioUrl.split('?')[1]);
      const audioId = urlParams.get('slide_id'); // Using slide_id parameter for audio files too
      
      // Construct the download URL for audio with cache busting
      const downloadUrl = `/download-audio?workshop_id=${encodeURIComponent(submittedWorkshopId)}&audio_id=${encodeURIComponent(audioId)}&t=${Date.now()}`;
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${studentId}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading MP3:', error);
      alert('Error downloading audio file. Please try again.');
    }
  };

  // While the list of workshops is loading, show a full-page spinner.
  const isFilmSlideshow = window.location.pathname.includes('film');

  const orangeHeader = (
    <div ref={orangeHeaderRef} style={{
      position: 'sticky',
      top: 0,
      zIndex: 1020,
      backgroundColor: '#f05f40',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
      padding: '0.26rem 0.85rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '0.38rem',
    }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flex: '1 1 auto',
          minWidth: 0,
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', position: 'relative' }}>
          <span
            ref={slideshowBrandRef}
            style={{ color: 'white', fontSize: '12px', fontWeight: 'bold', lineHeight: '1.15', fontFamily: 'Roboto, sans-serif' }}
          >
            {props.config.brand}
          </span>
          <span
            aria-hidden
            ref={slideshowSubtitleNaturalRef}
            style={{
              position: 'absolute',
              left: 0,
              top: '1.15em',
              visibility: 'hidden',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold',
              lineHeight: '1.15',
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            {props.config.subtitle}
          </span>
          <span
            style={{
              color: 'white',
              fontSize: `${slideshowSubtitleFontPx}px`,
              fontWeight: 'bold',
              lineHeight: '1.15',
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            {props.config.subtitle}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '0', marginRight: '0' }}>
          <div style={{ width: '1px', height: '24px', backgroundColor: 'white', opacity: '0.7' }}></div>
        </div>
        <div
          className="slideshow-film-mission-toggle"
          style={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 0,
            flex: '0 1 auto',
            minWidth: 0,
            maxWidth: '100%',
            width: 'max-content',
          }}
        >
          {/*
            Native buttons (not Bootstrap .btn): large radii + small hit area made pills look like circles.
            Fixed 8px corner radius + min width/height keeps a rounded-rectangle segmented control at all widths.
          */}
          <button
            type="button"
            onClick={() => { window.location.href = '/ai-film-slideshow'; }}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              backgroundColor: isFilmSlideshow ? 'white' : 'transparent',
              border: '1px solid white',
              borderRight: 'none',
              color: isFilmSlideshow ? '#f05f40' : SLIDESHOW_HEADER_UNSELECTED_TEXT,
              borderTopLeftRadius: '8px',
              borderBottomLeftRadius: '8px',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              margin: 0,
              padding: '0.12rem 0.4rem',
              fontSize: 'clamp(0.88rem, 1.9vw, 1.1rem)',
              fontWeight: isFilmSlideshow ? 700 : 300,
              fontFamily: '"Roboto", sans-serif',
              lineHeight: 1.12,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              cursor: 'pointer',
              minHeight: '1.88rem',
              boxSizing: 'border-box',
              flex: '1 1 0',
              minWidth: 'min-content',
            }}
          >
            Film Slideshow
          </button>
          <button
            type="button"
            onClick={() => { window.location.href = '/ai-mission-slideshow'; }}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              backgroundColor: !isFilmSlideshow ? 'white' : 'transparent',
              border: '1px solid white',
              color: !isFilmSlideshow ? '#f05f40' : SLIDESHOW_HEADER_UNSELECTED_TEXT,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '8px',
              margin: 0,
              padding: '0.12rem 0.4rem',
              fontSize: 'clamp(0.88rem, 1.9vw, 1.1rem)',
              fontWeight: !isFilmSlideshow ? 700 : 300,
              fontFamily: '"Roboto", sans-serif',
              lineHeight: 1.12,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              cursor: 'pointer',
              minHeight: '1.88rem',
              boxSizing: 'border-box',
              flex: '1 1 0',
              minWidth: 'min-content',
            }}
          >
            Mission Slideshow
          </button>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem',
          flexShrink: 0,
          justifyContent: 'flex-end',
        }}
      >
        {submittedWorkshopId != null && (
          <button
            type="button"
            onClick={() => setSubmittedWorkshopId(null)}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid white',
              color: 'white',
              borderRadius: '8px',
              margin: 0,
              padding: '0.08rem 0.45rem',
              fontSize: 'clamp(0.65rem, 2vw, 0.8rem)',
              fontWeight: 600,
              fontFamily: 'Roboto, sans-serif',
              lineHeight: 1.05,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              minHeight: '1.45rem',
              boxSizing: 'border-box',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.28rem',
              flexShrink: 0,
            }}
          >
            <FaArrowLeft style={{ flexShrink: 0 }} aria-hidden />
            Back to Workshop Selection
          </button>
        )}
      </div>
    </div>
  );

  const slideshowPageFooter = (
    <footer
      className="slideshow-page-footer py-3 mt-4"
      role="contentinfo"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', paddingLeft: '12px', paddingRight: '12px' }}
    >
      <div
        className="gear-icon"
        onClick={openManageServers}
        style={{
          position: 'absolute',
          left: '12px',
          fontSize: '16px',
          color: 'white',
          cursor: 'pointer',
          opacity: 0.15,
        }}
      >
        <FaCog />
      </div>
      <span className="copyright-text">© 2026 Stage One Education</span>
    </footer>
  );

  if (allWorkshopIds === null) {
    return (
      <SlideshowPageRoot useMissionBackground={isMissionSlideshow} useFilmBackground={isFilmWorkshopSlideshow}>
        <>
          {orangeHeader}
          <div className="d-flex justify-content-center align-items-center vh-100">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading workshops...</span>
            </div>
          </div>
          {slideshowPageFooter}
        </>
      </SlideshowPageRoot>
    );
  }

  if (workshopLoadError) {
    return (
      <SlideshowPageRoot useMissionBackground={isMissionSlideshow} useFilmBackground={isFilmWorkshopSlideshow}>
        <>
          {orangeHeader}
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ paddingTop: '100px' }}>
            <div className="alert alert-warning" style={{ maxWidth: '600px', textAlign: 'center' }}>
              <h5>Unable to load workshops</h5>
              <p>{workshopLoadError}</p>
              <p className="mb-0"><small>Log in at <a href="/portal">/portal</a> first, then return to this page.</small></p>
            </div>
          </div>
          {slideshowPageFooter}
        </>
      </SlideshowPageRoot>
    );
  }

  if (allWorkshopIds.length === 0) {
    return (
      <SlideshowPageRoot useMissionBackground={isMissionSlideshow} useFilmBackground={isFilmWorkshopSlideshow}>
        <>
          {orangeHeader}
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ paddingTop: '100px' }}>
            <div className="alert alert-info" style={{ maxWidth: '600px', textAlign: 'center' }}>
              <h5>No active workshops</h5>
              <p>There are no active workshops to display. Add workshops on the management page.</p>
            </div>
          </div>
          {slideshowPageFooter}
        </>
      </SlideshowPageRoot>
    );
  }

  let configSection = <></>;

  if (submittedWorkshopId === null) {
    configSection = (
      <div className="container my-4">
        {/* Config Section */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h4 className="card-title mb-4 text-center">Select a Workshop</h4>
            
            {/* Settings Toggles */}
            <div className="mb-4">
              <div className="d-flex justify-content-center align-items-center gap-4">
                <Form.Check
                  type="switch"
                  id="blank-detection-toggle"
                  label="Enable blank slide detection"
                  checked={blankDetectionEnabled}
                  onChange={(e) => setBlankDetectionEnabled(e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  id="inactive-workshops-toggle"
                  label="View inactive workshops"
                  checked={showInactiveWorkshops}
                  onChange={(e) => setShowInactiveWorkshops(e.target.checked)}
                />
              </div>
            </div>
            
            {!allWorkshopIds || allWorkshopIds.length === 0 ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading workshops...</span>
                </div>
                <p className="mt-3 text-muted">Loading available workshops...</p>
              </div>
            ) : (
              <Row className="g-3">
                {allWorkshopIds.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).map((id) => (
                  <Col key={id} xs={12} sm={6} md={4} lg={3}>
                    <Card 
                      className="h-100 workshop-card"
                      style={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        border: '1px solid #dee2e6'
                      }}
                      onClick={() => {
                        setSubmittedWorkshopId(id);
                        setBlankSlides([]); // Clear blank slides state when selecting a new workshop
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                      }}
                    >
                      <Card.Body className="d-flex flex-column justify-content-center align-items-center text-center p-4">
                        <div className="mb-3">
                          <FaList style={{ fontSize: '2rem', color: '#6c757d' }} />
                        </div>
                        <h6 className="card-title mb-0 fw-bold">{id}</h6>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <SlideshowPageRoot useMissionBackground={isMissionSlideshow} useFilmBackground={isFilmWorkshopSlideshow}>
    <>
      {orangeHeader}
      {/* Hidden audio element for playback */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        style={{ display: 'none' }}
      />
      
      {configSection}
      <div className="container mb-4 mt-0">
        {/* Slide Show Section */}
        {submittedWorkshopId &&
          (loadingSlides ? (
            <div
              className="d-flex justify-content-center align-items-center pt-4"
              style={{ height: "400px" }}
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading slides...</span>
              </div>
            </div>
          ) : slideLoadError ? (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ paddingTop: '60px' }}>
              <div className="alert alert-danger" style={{ maxWidth: '600px', textAlign: 'center' }}>
                <h5>Error loading slides</h5>
                <p>{slideLoadError}</p>
                <Button variant="outline-danger" onClick={handleRefreshSlides}>
                  <FaSync className="me-2" /> Try Again
                </Button>
              </div>
            </div>
          ) : (
            <>
            <div>
              <Card
                className="mb-4 slideshow-workshop-overview-card"
                style={{
                  overflow: "visible",
                  backgroundColor: "transparent",
                }}
              >
                <div
                  style={{
                    position: "sticky",
                    top: workshopCardHeaderStickyTopPx,
                    zIndex: 1015,
                  }}
                >
                  <div
                    aria-hidden
                    className={
                      isMissionSlideshow
                        ? "slideshow-overview-sticky-gap-mission"
                        : "slideshow-overview-sticky-gap-film"
                    }
                    style={{
                      height: WORKSHOP_OVERVIEW_TOP_GAP,
                      minHeight: WORKSHOP_OVERVIEW_TOP_GAP,
                    }}
                  />
                  <Card.Header
                    className="border-bottom py-1"
                    style={{
                      backgroundColor: "#fff",
                    }}
                  >
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-1">
                      <span className="mb-0 fw-semibold" style={{ color: '#424242', fontSize: '0.7rem' }}>
                        Workshop: {submittedWorkshopId}
                      </span>
                      <div className="d-flex flex-wrap align-items-center gap-2 ms-auto justify-content-end">
                        <span className="text-muted small">
                          {images.length} Active User{images.length !== 1 ? "s" : ""}
                        </span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          type="button"
                          className="text-nowrap"
                          disabled={overviewRefreshBusy}
                          title={
                            isMissionSlideshow
                              ? "Refresh slides and mission analytics"
                              : "Refresh slides list"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefreshOverview();
                          }}
                        >
                          <FaSync
                            className={`me-1 ${overviewRefreshBusy ? "fa-spin" : ""}`}
                          />
                          {overviewRefreshBusy ? "Refreshing..." : "Refresh"}
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                </div>
                <Card.Body
                  className="bg-white slideshow-workshop-overview-card-body"
                  style={{ backgroundColor: "#fff" }}
                >
              {showSlideshow && (
                <div ref={slideshowViewerRef} className="mb-3">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <Button
                      variant="secondary"
                      className="text-nowrap flex-shrink-0"
                      size="sm"
                      onClick={exitSlideshowToOverview}
                    >
                      <FaArrowLeft className="me-1" /> Close
                    </Button>

                    <div className="text-center flex-grow-1 px-1" style={{ minWidth: '8rem' }}>
                      <small className="text-muted">
                        {getFilteredSlides().length} of {images.length} slides
                        {blankDetectionEnabled && blankSlides.length > 0 && ` • ${blankSlides.length} blank slides skipped`}
                      </small>
                    </div>

                    <div className="d-flex gap-2 flex-shrink-0 flex-wrap justify-content-end">
                      <button
                        type="button"
                        onClick={enterFullscreenSlideshow}
                        style={VIEW_SLIDESHOW_BTN_STYLE}
                      >
                        <FaExpand className="me-2" style={{ flexShrink: 0 }} />
                        View Slideshow
                      </button>
                    </div>
                  </div>

                  <Card className="mb-3">
                    <Card.Body className="py-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          {currentAudio ? (
                            <>
                              <span className="small">
                                {getFilteredSlides().length > 0 && currentSlideIndex < getFilteredSlides().length ?
                                  extractSlideNumber(getFilteredSlides()[currentSlideIndex].original) : 'No slide'}
                                <span style={{ marginLeft: '20px', marginRight: '-3px' }}>
                                  Audio:
                                </span>
                                <span className="text-muted ms-2">
                                  ({formatDuration(audioDurations[currentAudio])})
                                </span>
                              </span>
                            </>
                          ) : (
                            <span className="small">
                              {getFilteredSlides().length > 0 && currentSlideIndex < getFilteredSlides().length ?
                                extractSlideNumber(getFilteredSlides()[currentSlideIndex].original) : 'No slide'}
                            </span>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              if (isAudioPlaying) {
                                stopAudio();
                              } else {
                                playAudio(currentAudio);
                              }
                            }}
                            disabled={!currentAudio}
                          >
                            {isAudioPlaying ? (
                              <>
                                <FaStop className="me-1" />
                                Stop
                              </>
                            ) : (
                              <>
                                <FaPlay className="me-1" />
                                Play
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>

                  <ImageGallery
                    items={getFilteredSlides()}
                    showPlayButton={false}
                    showNav={true}
                    showThumbnails={false}
                    infinite={false}
                    slideDuration={100}
                    startIndex={currentSlideIndex}
                    onSlide={(index) => setCurrentSlideIndex(index)}
                    renderCustomControls={() => {
                      const filtered = getFilteredSlides();
                      const nodeLabel =
                        filtered.length > 0 && currentSlideIndex < filtered.length
                          ? extractSlideNumber(filtered[currentSlideIndex].original)
                          : '';
                      if (!nodeLabel) return null;
                      const missionInfo = isMissionSlideshow ? getMissionByNode(nodeLabel) : null;
                      return (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '12px',
                            left: '12px',
                            zIndex: 10,
                            fontSize: '0.88rem',
                            color: '#333',
                            backgroundColor: 'transparent',
                            borderRadius: '4px',
                            padding: '2px 7px',
                            pointerEvents: 'none',
                            fontFamily: 'Roboto, sans-serif',
                            lineHeight: 1.4,
                          }}
                        >
                          <div>{nodeLabel}</div>
                          {missionInfo && (
                            <div style={{ fontSize: '0.78rem' }}>{missionInfo.name}</div>
                          )}
                          <div style={{ fontSize: '0.78rem' }}>{submittedWorkshopId}</div>
                        </div>
                      );
                    }}
                  />

                  {getFilteredSlides().length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-muted">No slides available for slideshow.</p>
                    </div>
                  )}
                </div>
              )}
              {isMissionSlideshow && (
                <Card className="mb-3">
                  <Card.Header>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={analyticsExpanded}
                      aria-label={analyticsExpanded ? "Collapse analytics" : "Expand analytics"}
                      onClick={() => setAnalyticsExpanded((v) => !v)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setAnalyticsExpanded((v) => !v);
                        }
                      }}
                      className="d-flex align-items-center min-w-0"
                      style={{ cursor: "pointer" }}
                    >
                      <h5 className="mb-0 d-flex align-items-center gap-2">
                        {analyticsExpanded ? (
                          <FaChevronDown className="text-secondary flex-shrink-0" aria-hidden />
                        ) : (
                          <FaChevronRight className="text-secondary flex-shrink-0" aria-hidden />
                        )}
                        Analytics
                      </h5>
                    </div>
                  </Card.Header>
                  <Collapse in={analyticsExpanded}>
                    <div>
                      <Card.Body>
                    <div>
                      {ratingCountsError && (
                        <p className="small text-danger mb-3">{ratingCountsError}</p>
                      )}
                      {ratingCountsLoading && !ratingCountsError && (
                        <div className="text-muted small py-3">Loading chart…</div>
                      )}
                      {!ratingCountsLoading && !ratingCountsError && (
                        <div
                          className="mx-auto rounded-4 overflow-hidden"
                          style={{
                            width: "100%",
                            maxWidth: ANALYTICS_CHART_MAX_WIDTH,
                            backgroundColor: "#fff",
                            border: `1px solid ${SLIDESHOW_ORANGE}`,
                            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
                          }}
                        >
                          <div
                            className="px-3 pt-3 pb-2"
                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.07)" }}
                          >
                            <h6
                              className="mb-1 fw-semibold text-body"
                              style={{ fontSize: "1.05rem", letterSpacing: "-0.02em", lineHeight: 1.25 }}
                            >
                              Image Realism Rating
                            </h6>
                            <p className="text-muted small mb-1 mb-md-2" style={{ lineHeight: 1.45 }}>
                              One rating per participant
                            </p>
                            <p className="small mb-0" style={{ color: "#5c5c5c" }}>
                              <span className="fw-semibold" style={{ color: SLIDESHOW_ORANGE }}>
                                {ratingTotal}
                              </span>
                              <span className="text-muted">
                                {" "}
                                {ratingTotal === 1 ? "participant" : "participants"}
                              </span>
                            </p>
                          </div>
                          <div className="d-flex flex-column gap-2 p-3">
                            {MISSION_IMAGE_RATING_LABELS.map((label, idx) => {
                              const key = String(idx + 1);
                              const c = Number(ratingCounts[key] ?? 0);
                              const pctBar =
                                ratingTotal > 0 ? Math.round((c / ratingTotal) * 1000) / 10 : 0;
                              const pctShare =
                                ratingTotal > 0 ? Math.round((c / ratingTotal) * 100) : 0;
                              return (
                                <div key={key}>
                                  <div className="d-flex justify-content-between align-items-baseline gap-3 mb-1">
                                    <span className="small text-body" style={{ lineHeight: 1.3, fontWeight: 500 }}>
                                      <span className="text-secondary me-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                                        {key}
                                      </span>
                                      {label}
                                    </span>
                                    <span
                                      className="small text-secondary text-nowrap"
                                      style={{ fontVariantNumeric: "tabular-nums" }}
                                      title={`${pctShare}% of responses`}
                                    >
                                      {c}
                                      <span className="ms-1" style={{ color: SLIDESHOW_ORANGE, fontWeight: 600 }}>
                                        {ratingTotal > 0 ? `${pctShare}%` : "—"}
                                      </span>
                                    </span>
                                  </div>
                                  <div
                                    className="progress rounded-pill overflow-hidden"
                                    style={{
                                      height: ANALYTICS_BAR_TRACK_HEIGHT,
                                      backgroundColor: "#eceff1",
                                      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
                                    }}
                                  >
                                    <div
                                      className="progress-bar rounded-pill"
                                      role="progressbar"
                                      style={{
                                        width: `${pctBar}%`,
                                        backgroundColor: SLIDESHOW_ORANGE,
                                        backgroundImage: "none",
                                      }}
                                      aria-valuenow={c}
                                      aria-valuemin={0}
                                      aria-valuemax={Math.max(ratingTotal, 1)}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-5 pt-4 border-top" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                      {budgetAvgError && (
                        <p className="small text-danger mb-3">{budgetAvgError}</p>
                      )}
                      {budgetAvgLoading && !budgetAvgError && (
                        <div className="text-muted small py-3">Loading chart…</div>
                      )}
                      {!budgetAvgLoading && !budgetAvgError && budgetAvgCount === 0 && (
                        <p className="small text-muted mb-0">
                          No budget data yet (participants must submit exactly 100% on Mission Budget).
                        </p>
                      )}
                      {!budgetAvgLoading && !budgetAvgError && budgetAvgCount > 0 && (
                        <div
                          className="mx-auto rounded-4 overflow-hidden"
                          style={{
                            width: "100%",
                            maxWidth: ANALYTICS_CHART_MAX_WIDTH,
                            backgroundColor: "#fff",
                            border: `1px solid ${SLIDESHOW_ORANGE}`,
                            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06)",
                          }}
                        >
                          <div
                            className="px-3 pt-3 pb-2"
                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.07)" }}
                          >
                            <h6
                              className="mb-1 fw-semibold text-body"
                              style={{ fontSize: "1.05rem", letterSpacing: "-0.02em", lineHeight: 1.25 }}
                            >
                              Budget Breakdown
                            </h6>
                            <p className="text-muted small mb-1 mb-md-2" style={{ lineHeight: 1.45 }}>
                              Average allocation (first successful 100% submit per participant on Mission Budget).
                            </p>
                            <p className="small mb-0" style={{ color: "#5c5c5c" }}>
                              <span className="fw-semibold" style={{ color: SLIDESHOW_ORANGE }}>
                                {budgetAvgCount}
                              </span>
                              <span className="text-muted">
                                {" "}
                                participant{budgetAvgCount !== 1 ? "s" : ""}
                              </span>
                            </p>
                          </div>
                          <div className="d-flex flex-column gap-2 p-3">
                            {MISSION_BUDGET_GRAPH_SYSTEMS.map(({ key, label }) => {
                              const avg = Number(budgetAverages[key] ?? 0);
                              const pctBar = Math.min(100, Math.max(0, avg));
                              return (
                                <div key={key}>
                                  <div className="d-flex justify-content-between align-items-baseline gap-3 mb-1">
                                    <span className="small text-body" style={{ lineHeight: 1.3, fontWeight: 500 }}>
                                      {label}
                                    </span>
                                    <span
                                      className="small text-nowrap"
                                      style={{ fontVariantNumeric: "tabular-nums", color: SLIDESHOW_ORANGE, fontWeight: 600 }}
                                    >
                                      {avg % 1 === 0 ? avg : avg.toFixed(1)}%
                                    </span>
                                  </div>
                                  <div
                                    className="progress rounded-pill overflow-hidden"
                                    style={{
                                      height: ANALYTICS_BAR_TRACK_HEIGHT,
                                      backgroundColor: "#eceff1",
                                      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
                                    }}
                                  >
                                    <div
                                      className="progress-bar rounded-pill"
                                      role="progressbar"
                                      style={{
                                        width: `${pctBar}%`,
                                        backgroundColor: SLIDESHOW_ORANGE,
                                        backgroundImage: "none",
                                      }}
                                      aria-valuenow={pctBar}
                                      aria-valuemin={0}
                                      aria-valuemax={100}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                      </Card.Body>
                    </div>
                  </Collapse>
                </Card>
              )}

              <Card className="mb-0">
                <Card.Header>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h5 className="mb-0">Posters</h5>
                    <div className="d-flex flex-wrap gap-2 align-items-center justify-content-end ms-auto">
                      <Form.Check
                        type="switch"
                        id="blank-detection-toggle-posters"
                        className="mb-0"
                        label="Enable blank slide detection"
                        checked={blankDetectionEnabled}
                        onChange={(e) => {
                          setBlankDetectionEnabled(e.target.checked);
                          if (e.target.checked && images.length > 0) {
                            const imageSlides = images.map((img) => img.original);
                            analyzeSlidesForBlank(imageSlides);
                          } else {
                            setBlankSlides([]);
                          }
                        }}
                      />
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="text-nowrap"
                        type="button"
                        onClick={downloadSlideshowAsPDF}
                        disabled={downloadingPDF || images.length === 0}
                      >
                        <FaDownload className={`me-2 ${downloadingPDF ? "fa-spin" : ""}`} />
                        {downloadingPDF ? "Generating PDF..." : "Download PDF"}
                      </Button>
                      <button
                        type="button"
                        onClick={enterFullscreenSlideshow}
                        style={VIEW_SLIDESHOW_BTN_STYLE}
                      >
                        <FaExpand className="me-2" style={{ flexShrink: 0 }} />
                        View Slideshow
                      </button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  {refreshingSlides ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">
                          Refreshing slides...
                        </span>
                      </div>
                      Refreshing slides...
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <strong>Submissions:</strong> {submissions.length} students
                        {blankDetectionEnabled && blankSlides.length > 0 && (
                          <span className="ms-3">
                            <Badge bg="warning" text="dark">
                              {blankSlides.length} blank slide{blankSlides.length !== 1 ? 's' : ''}
                            </Badge>
                          </span>
                        )}
                      </div>
                      
                      {blankDetectionEnabled && blankSlides.length > 0 && (
                        <div className="mb-3">
                          <strong>Blank/White Slides:</strong> {blankSlides.map(index => extractSlideNumber(images[index - 1]?.original || 'Unknown')).join(", ")}
                        </div>
                      )}
                      
                      {submissions.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-muted">No submissions found for this workshop.</p>
                        </div>
                      ) : (
                        <Row>
                          {submissions.map((submission, index) => {
                            const slideNumber = submission.slide ? extractSlideNumber(submission.slide) : 'No slide';
                            const hasSlide = submission.slide !== null;
                            const hasAudio = submission.audio !== null;
                            const isCurrentAudio = currentAudio === submission.audio;
                            
                            return (
                              <Col key={index} xs={12} sm={6} md={4} lg={3} className="mb-4">
                                <Card className="h-100">
                                  <Card.Body className="d-flex flex-column">
                                    {hasSlide ? (
                                      (() => {
                                        const imgIdx = images.findIndex(img => extractSlideNumber(img.original) === submission.student_id);
                                        const isBlank = imgIdx !== -1 && blankSlides.includes(imgIdx + 1);
                                        return (
                                      <div 
                                        className="mb-3"
                                        style={{ 
                                          cursor: 'pointer',
                                          width: '100%'
                                        }}
                                        onClick={() => {
                                          if (imgIdx !== -1) {
                                            handleSlideClick(imgIdx);
                                          }
                                        }}
                                      >
                                        <img 
                                          src={`${submission.slide}${submission.slide.includes('?') ? '&' : '?'}t=${cacheBuster}`}
                                          alt={`Slide ${slideNumber}`}
                                          style={{ 
                                            width: '100%',
                                            height: '150px',
                                            objectFit: 'contain',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '4px',
                                            border: isBlank ? '2px solid #ffc107' : 'none'
                                          }}
                                        />
                                        
                                        {/* Label below the image */}
                                        <div 
                                          className="d-flex justify-content-center align-items-center mt-2"
                                          style={{
                                            padding: '4px 8px'
                                          }}
                                        >
                                          <small className="text-dark fw-bold mb-0">
                                            {slideNumber}
                                            {isBlank && (
                                              <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.6em' }}>
                                                Blank
                                              </Badge>
                                            )}
                                          </small>
                                        </div>
                                      </div>
                                        );
                                      })()
                                    ) : (
                                      <div className="mb-3 text-center py-4">
                                        <small className="text-muted">No slide submitted</small>
                                      </div>
                                    )}
                                    
                                    {hasAudio && (
                                      <div className="mt-auto">
                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                              if (isCurrentAudio && isAudioPlaying) {
                                                stopAudio();
                                              } else {
                                                playAudio(submission.audio);
                                              }
                                            }}
                                          >
                                            {isCurrentAudio && isAudioPlaying ? (
                                              <>
                                                <FaStop className="me-1" />
                                                Stop
                                              </>
                                            ) : (
                                              <>
                                                <FaPlay className="me-1" />
                                                Play
                                              </>
                                            )}
                                          </Button>
                                          {audioDurations[submission.audio] && (
                                            <small className="text-muted">
                                              {formatDuration(audioDurations[submission.audio])}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </Card.Body>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
                </Card.Body>
              </Card>
            </div>
            </>
          ))}
      </div>
      {slideshowPageFooter}
    </>
    </SlideshowPageRoot>
  );
};

export { Slideshow };
